import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/useAuth';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import {
    RefreshCw,
    Search,
    Edit2,
    Loader2,
    Check,
    X,
    UserCheck,
    Link2,
    Link2Off,
    ExternalLink,
} from 'lucide-react';
import type { ApiResponse, Client, ClientAccount, ClientAccountSyncStatus } from '../types';
import { config } from '../config';

export function ClientPortal() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { isAdmin } = useAuth();
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'linked' | 'unlinked'>('all');
    const [associatingAccount, setAssociatingAccount] = useState<ClientAccount | null>(null);

    // Fetch client accounts
    const { data: accountsData, isLoading: accountsLoading } = useQuery({
        queryKey: ['client-portal', 'client-accounts'],
        queryFn: () => api.get<ApiResponse<ClientAccount[]>>('/api/admin/client-accounts'),
    });

    // Fetch clients for association dropdown
    const { data: clientsData } = useQuery({
        queryKey: ['client-portal', 'clients'],
        queryFn: () => api.get<ApiResponse<Client[]>>('/api/clients'),
    });

    // Fetch sync status
    const { data: syncStatusData } = useQuery({
        queryKey: ['client-portal', 'client-accounts', 'sync-status'],
        queryFn: () => api.get<ApiResponse<ClientAccountSyncStatus>>('/api/admin/client-accounts/sync/status'),
    });

    const accounts = accountsData?.data || [];
    const clients = clientsData?.data || [];
    const syncStatus = syncStatusData?.data;

    // Filter accounts
    const filteredAccounts = accounts.filter((account) => {
        const searchLower = search.toLowerCase();
        const matchesSearch =
            account.displayName.toLowerCase().includes(searchLower) ||
            account.email.toLowerCase().includes(searchLower) ||
            (account.companyName?.toLowerCase().includes(searchLower) ?? false);

        const matchesFilter =
            filterStatus === 'all' ||
            (filterStatus === 'linked' && account.clientId) ||
            (filterStatus === 'unlinked' && !account.clientId);

        return matchesSearch && matchesFilter;
    });

    // Sync mutation
    const syncMutation = useMutation({
        mutationFn: () => api.post('/api/admin/client-accounts/sync'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['client-portal', 'client-accounts'] });
        },
    });

    // Associate mutation
    const associateMutation = useMutation({
        mutationFn: ({ accountId, clientId }: { accountId: string; clientId: string | null }) =>
            api.put(`/api/admin/client-accounts/${accountId}/associate`, { clientId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['client-portal', 'client-accounts'] });
            setAssociatingAccount(null);
        },
    });

    // Toggle active mutation
    const toggleActiveMutation = useMutation({
        mutationFn: (accountId: string) =>
            api.put(`/api/admin/client-accounts/${accountId}/toggle-active`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['client-portal', 'client-accounts'] });
        },
    });

    const linkedCount = accounts.filter((a) => a.clientId).length;
    const unlinkedCount = accounts.filter((a) => !a.clientId).length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-[var(--color-text)] flex items-center gap-3">
                    <UserCheck className="w-7 h-7 text-[var(--color-primary)]" />
                    Portail Client
                </h1>
                <p className="text-[var(--color-text-secondary)]">
                    Gérez les comptes clients et leurs associations aux projets
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Total comptes</div>
                    <div className="text-2xl font-bold text-[var(--color-text)]">{accounts.length}</div>
                </div>
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Associés</div>
                    <div className="text-2xl font-bold text-[var(--color-success)]">{linkedCount}</div>
                </div>
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Non associés</div>
                    <div className="text-2xl font-bold text-[var(--color-warning)]">{unlinkedCount}</div>
                </div>
                <div className="card">
                    <div className="text-sm text-[var(--color-text-secondary)]">Dernière sync</div>
                    <div className="text-lg font-medium text-[var(--color-text)]">
                        {syncStatus?.lastSyncAt
                            ? format(new Date(syncStatus.lastSyncAt), 'dd/MM HH:mm')
                            : 'Jamais'}
                    </div>
                </div>
            </div>

            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input pl-9 w-full sm:w-64"
                        />
                    </div>

                    {/* Filter */}
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as 'all' | 'linked' | 'unlinked')}
                        className="input"
                    >
                        <option value="all">Tous</option>
                        <option value="linked">Associés</option>
                        <option value="unlinked">Non associés</option>
                    </select>
                </div>

                {/* Sync Button - Admin only */}
                {isAdmin && (
                    <button
                        onClick={() => syncMutation.mutate()}
                        disabled={syncMutation.isPending}
                        className="btn-primary flex items-center gap-2"
                    >
                        <RefreshCw className={cn('w-4 h-4', syncMutation.isPending && 'animate-spin')} />
                        Synchroniser depuis Entra ID
                    </button>
                )}
            </div>

            {/* Info message */}
            <div className="bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <ExternalLink className="w-5 h-5 text-[var(--color-primary)] mt-0.5" />
                    <div>
                        <p className="text-sm text-[var(--color-text)]">
                            <strong>Portail Client :</strong> Les comptes listés ici proviennent de l'application entreprise Entra ID dédiée aux clients.
                            {isAdmin
                                ? 'Associez chaque compte à un client CRA pour leur donner accès au reporting de leurs projets.'
                                : "L'association d'un compte à un client CRA, qui lui ouvre le reporting de ses projets, est réservée aux administrateurs."}
                        </p>
                        {config.reportingUrl && (
                            <a
                                href={config.reportingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[var(--color-primary)] hover:underline mt-1 inline-block"
                            >
                                Accéder au portail client →
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Accounts Table */}
            <div className="card !p-0 overflow-hidden">
                {accountsLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
                    </div>
                ) : filteredAccounts.length === 0 ? (
                    <div className="text-center py-12 text-[var(--color-text-secondary)]">
                        {search || filterStatus !== 'all'
                            ? 'Aucun compte ne correspond aux critères'
                            : 'Aucun compte client. Lancez une synchronisation depuis Entra ID.'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-secondary)]">
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Utilisateur
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Email
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Société
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Client associé
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Statut
                                    </th>
                                    <th className="text-left p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Dernière connexion
                                    </th>
                                    <th className="text-right p-4 text-sm font-medium text-[var(--color-text-secondary)]">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAccounts.map((account) => (
                                    <tr
                                        key={account.id}
                                        className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-secondary)]/50"
                                    >
                                        <td className="p-4">
                                            <div className="font-medium text-[var(--color-text)]">
                                                {account.displayName}
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm text-[var(--color-text-secondary)]">
                                            {account.email}
                                        </td>
                                        <td className="p-4 text-sm text-[var(--color-text-secondary)]">
                                            {account.companyName || '-'}
                                        </td>
                                        <td className="p-4">
                                            {associatingAccount?.id === account.id ? (
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        className="input text-sm py-1"
                                                        defaultValue={account.clientId || ''}
                                                        onChange={(e) => {
                                                            associateMutation.mutate({
                                                                accountId: account.id,
                                                                clientId: e.target.value || null,
                                                            });
                                                        }}
                                                    >
                                                        <option value="">-- Aucun --</option>
                                                        {clients.map((client) => (
                                                            <option key={client.id} value={client.id}>
                                                                {client.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={() => setAssociatingAccount(null)}
                                                        className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : account.clientId ? (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-success)]/10 text-[var(--color-success)]">
                                                    <Link2 className="w-3 h-3" />
                                                    {account.clientName}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-warning)]/10 text-[var(--color-warning)]">
                                                    <Link2Off className="w-3 h-3" />
                                                    Non associé
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span
                                                className={cn(
                                                    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                                                    account.active
                                                        ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                                                        : 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
                                                )}
                                            >
                                                {account.active ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-[var(--color-text-secondary)]">
                                            {account.lastLogin
                                                ? format(new Date(account.lastLogin), 'dd/MM/yyyy HH:mm')
                                                : 'Jamais'}
                                        </td>
                                        <td className="p-4">
                                            {/* Association et activation réservées aux administrateurs (#47) :
                                                l'API refuse les autres rôles. */}
                                            {isAdmin && (
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => setAssociatingAccount(account)}
                                                    className="p-2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 rounded-lg transition-colors"
                                                    title="Associer à un client"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => toggleActiveMutation.mutate(account.id)}
                                                    disabled={toggleActiveMutation.isPending}
                                                    className={cn(
                                                        'p-2 rounded-lg transition-colors',
                                                        account.active
                                                            ? 'text-[var(--color-error)] hover:bg-[var(--color-error)]/10'
                                                            : 'text-[var(--color-success)] hover:bg-[var(--color-success)]/10'
                                                    )}
                                                    title={account.active ? 'Désactiver' : 'Activer'}
                                                >
                                                    {account.active ? (
                                                        <X className="w-4 h-4" />
                                                    ) : (
                                                        <Check className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
